import { AfterViewInit, Component, inject, NgZone, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Data, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Constants } from "@ramudden/core/constants";
import { dayjs } from "@ramudden/core/utils";
import { IEnvironment } from "@ramudden/models/environment";
import { IOrganization } from "@ramudden/models/user";
import {
    ConfigurationService,
    LocalStorageService,
    PromptUpdateService,
    TitleService,
    WebsiteService,
} from "@ramudden/services";
import { ModalConfig, ModalService } from "@ramudden/ui";
import Bowser from "bowser";
import { firstValueFrom, ReplaySubject } from "rxjs";
import { filter, map, mergeMap, take } from "rxjs/operators";
import { environment } from "../environments/environment";
import { CookieNotificationComponent } from "./components/shared/cookie-notification/cookie-notification.component";
import { PageBodyComponent } from "./layout/page/page-body/page-body.component";
import { PageHeaderComponent } from "./layout/page/page-header/page-header.component";
import { PageComponent } from "./layout/page/page.component";
import { AuthenticationService } from "./services/authentication.service";
import { DomainDataService } from "./services/domain-data.service";
import { GalleriaService } from "./services/galleria.service";
import { GlobalStateService } from "./services/global-state.service";
import { MapIconService } from "./services/map-icon.service";
import { MomentService } from "./services/moment.service";
import { NavigationService } from "./services/navigation.service";
import { DomainDataRealtimeService } from "./services/realtime/domain-data-realtime.service";
import { GalleriaComponent } from "./shared/components/galleria/galleria.component";
import { ProgressComponent } from "./shared/components/progress/progress.component";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    imports: [
        PageComponent,
        PageHeaderComponent,
        PageBodyComponent,
        CookieNotificationComponent,
        ProgressComponent,
        GalleriaComponent,
    ],
})
export class AppComponent implements OnInit, AfterViewInit {
    private static subjectConfigLoaded$ = new ReplaySubject<void>(1);

    @ViewChild("modalContainer", { read: ViewContainerRef, static: true }) modalContainer!: ViewContainerRef;
    @ViewChild(GalleriaComponent, { static: true }) galleriaComponent: GalleriaComponent;

    environment: IEnvironment;
    isIframe: boolean;

    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly titleService = inject(TitleService);
    private readonly translate = inject(TranslateService);
    private readonly zone = inject(NgZone);
    private readonly galleriaService = inject(GalleriaService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly globalStateService = inject(GlobalStateService);
    private readonly title = inject(Title);
    private readonly websiteService = inject(WebsiteService);
    private readonly domainDataService = inject(DomainDataService);
    private readonly promptUpdateService = inject(PromptUpdateService);
    private readonly modalService = inject(ModalService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly domainDataRealtimeService = inject(DomainDataRealtimeService);
    private readonly mapIconService = inject(MapIconService);
    private readonly momentService = inject(MomentService);

    constructor() {
        this.promptUpdateService.subscribeToUpdates();

        this.environment = this.configurationService.configuration;

        this.loadGoogleTag();
        this.loadAdditionalStyleSheets();

        this.configureTranslations();
        this.overwriteEvents();

        this.title.setTitle(this.websiteService.getProductName());
    }

    static async initializeConfiguration(configService: ConfigurationService): Promise<void> {
        try {
            if (!configService.configuration) {
                await configService.loadConfiguration(environment);
            }

            // only when configuration have been loaded, start with authentication
            this.subjectConfigLoaded$.next();
        } catch (error) {
            if (!environment.production) {
                console.error(error);
            }
        }
    }

    static async initializeAuthentication(authenticationService: AuthenticationService) {
        await firstValueFrom(this.subjectConfigLoaded$);
        await authenticationService.initialize(); // will initialize domain data as well
    }

    ngOnInit() {
        this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

        // Fill in Title according to route.data.name
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd))
            .subscribe((event: NavigationStart | NavigationEnd) => {
                NavigationService.isNavigating = event instanceof NavigationStart;
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route: ActivatedRoute) => {
                    while (route.firstChild) route = route.firstChild;
                    return route;
                }),
                filter((route: ActivatedRoute) => route.outlet === "primary"),
                mergeMap((route: ActivatedRoute) => route.data),
            )
            .subscribe((data: Data) => {
                if (data["name"]) {
                    this.titleService.setTitle(data["name"]);
                }
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1),
            )
            .subscribe((_x) => {
                if (this.globalStateService.getIsAuthenticated()) {
                    this.authenticationService.tryRedirectToCallbackRoute();
                }
            });

        this.domainDataService.updateTranslations(); // SCS-666 Make sure translations are loaded properly, sanity check

        window.addEventListener("storage", (event: StorageEvent) => {
            this.zone.run(() => {
                if (event.storageArea === localStorage) {
                    let message = null;
                    if (event.key === Constants.roleImpersonationStorageKey) {
                        const role = this.domainDataService.translateEnum("roles", event.newValue);
                        if (role === null) {
                            message = this.translate.instant("impersonation.workingAsOwnRoleOtherTab");
                        } else {
                            message = this.translate.instant("impersonation.changeRoleOtherTab", { name: role });
                        }
                    }

                    if (event.key === Constants.organizationImpersonationStorageKey) {
                        const organization: IOrganization | null = JSON.parse(event.newValue);
                        if (organization === null) {
                            message = this.translate.instant("impersonation.workingAsOwnOrganizationOtherTab");
                        } else {
                            message = this.translate.instant("impersonation.changeOrganizationOtherTab", {
                                name: organization.name,
                            });
                        }
                    }

                    if (message !== null) {
                        const reload = () => {
                            window.location.reload();
                        };

                        const modalConfig: ModalConfig = {
                            canClose: false,
                            title: "general.info",
                            body: message,
                            type: "info",
                            actions: [{ label: "Ok", class: ["btn--yellow"], handler: reload }],
                        };

                        this.modalService.showModal(modalConfig);
                    }
                }
            });
        });
    }

    ngAfterViewInit() {
        this.modalService.setRootViewContainerRef(this.modalContainer);
        this.galleriaService.register(this.galleriaComponent);
        this.checkForValidBrowser();
        this.domainDataRealtimeService.subscribe();
        this.mapIconService.initializeService();
    }

    // private checkedCounter: number = 0;
    // ngDoCheck() {
    //     console.log("Checked", this.checkedCounter++);
    // }

    /**
     * Sets the Day.js locale for the app.
     */
    private applyLocale(locale: string) {
        dayjs.locale(locale);
    }

    private configureTranslations() {
        // this language will be used as a fallback when a translation isn't found in the current language
        this.translate.setDefaultLang(DomainDataService.defaultLanguage);

        this.translate.onLangChange.subscribe(() => {
            this.localStorageService.setItem("language", this.translate.currentLang);
            this.applyLocale(this.momentService.currentLocale);
        });

        // Resolve the language with the SAME logic used when domain data is loaded
        // (see AuthenticationService.authorize). Keeping these in sync guarantees the
        // current language is always present in the loaded domain data, so cached enum
        // view models never get undefined labels (rendered as "[object Object]").
        const userLanguage = this.globalStateService.getAuthorizationInfo()?.user?.languageId;
        this.translate.use(this.domainDataService.getInitialLanguage(userLanguage));
    }

    private overwriteEvents() {
        // platform-browser adds a `mousemove` event
        // this triggers change-detection ALL THE TIME
        // run it outside of Angular
        // Angular by itself attaches a `click` event (`wrapFn`)
        const originalDocumentListener = document ? document.addEventListener : null;
        if (originalDocumentListener) {
            document.addEventListener = (type: string, listener: any, options: any) => {
                if (type === "mousemove") {
                    this.zone.runOutsideAngular(() => {
                        originalDocumentListener.call(document, type, listener, options);
                    });
                    return;
                }

                originalDocumentListener.call(document, type, listener, options);
            };
        }
    }

    private loadGoogleTag() {
        if (!this.environment.googleTagKey) return;

        window["dataLayer"] = window["dataLayer"] || [];
        window["dataLayer"].push({
            "gtm.start": new Date().getTime(),
            event: "gtm.js",
        });

        const scriptNode = document.createElement("script");

        scriptNode.async = true;
        scriptNode.src = "https://www.googletagmanager.com/gtm.js?id=" + this.environment.googleTagKey;
        scriptNode.nonce = "tagmanager";
        document.head.appendChild(scriptNode);
    }

    private loadAdditionalStyleSheets() {
        if (!window) return;
    }

    private checkForValidBrowser() {
        const didGetNotificationStorageKey = "incompatibleBrowserNotification";
        const didGetNotificationAlready = !!this.localStorageService.getItem(didGetNotificationStorageKey);
        if (didGetNotificationAlready) return;

        // Check for valid browsers, if not, show a notification
        const bowser = Bowser.getParser(window.navigator.userAgent);
        bowser.getPlatformType();

        const microsoftEdge = "Microsoft Edge";
        const compatibleBrowsers = ["Chrome", "Firefox", "Opera", microsoftEdge];

        const showError = () => {
            this.localStorageService.setItem(didGetNotificationStorageKey, "true");

            setTimeout(() => {
                this.modalService.error("errors.incompatibleBrowser");
            });
        };

        const browserName = bowser.getBrowserName();
        if (!compatibleBrowsers.contains(browserName)) {
            showError();
        } else {
            if (browserName === microsoftEdge && bowser.getEngineName() !== "Blink") {
                showError();
            }
        }
    }
}
